import React, { useState, useEffect } from 'react';
import {
  Globe, Users, Server, CreditCard, TrendingUp, AlertTriangle,
  Activity, DollarSign, Calendar, ArrowUpRight, ArrowDownRight,
  Package, BarChart3, PieChart, TrendingDown, Wallet, Settings as SettingsIcon
} from 'lucide-react';
import { domainsAPI, customersAPI, serversAPI, paymentsAPI, companyPaymentsAPI, settingsAPI, accountTransactionsAPI, accountsAPI } from '../lib/api';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalDomains: 0,
    totalCustomers: 0,
    totalServers: 0,
    pendingPayments: 0,
    overduePayments: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0
  });

  const [loading, setLoading] = useState(true);
  const [, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });
  const [companyPayments, setCompanyPayments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [revenueByType, setRevenueByType] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showAccountWarning, setShowAccountWarning] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const results = await Promise.allSettled([
        domainsAPI.getAll(),
        customersAPI.getAll(),
        serversAPI.getAll(),
        paymentsAPI.getAll(),
        companyPaymentsAPI.getAll(),
        settingsAPI.getAll(),
        accountTransactionsAPI.getAll(),
        accountsAPI.getAll()
      ]);

      const [domainsResult, customersResult, serversResult, , companyPaymentsResult, settingsResult, transactionsResult, accountsResult] = results;

      const domains = domainsResult.status === 'fulfilled' ? domainsResult.value : [];
      const customers = customersResult.status === 'fulfilled' ? customersResult.value : [];
      const servers = serversResult.status === 'fulfilled' ? serversResult.value : [];
      const companyPayments = companyPaymentsResult.status === 'fulfilled' ? companyPaymentsResult.value : [];
      const transactions = transactionsResult.status === 'fulfilled' ? transactionsResult.value : [];
      const accounts = accountsResult.status === 'fulfilled' ? accountsResult.value : [];
      
      const settings = settingsResult.status === 'fulfilled' ? settingsResult.value : {};

      const rates = {
        USD: parseFloat(settings.rate_usd || settings.exchange_rate_usd) || 1,
        EUR: parseFloat(settings.rate_eur || settings.exchange_rate_eur) || 1,
        GBP: parseFloat(settings.rate_gbp || settings.exchange_rate_gbp) || 1,
        GOLD: parseFloat(settings.rate_gold || settings.exchange_rate_gold) || 1
      };
      setExchangeRates(rates);

      // Get default account for Dashboard
      const defaultAccountId = settings.dashboard_default_account || '';

      const convertToTRY = (amount, currency) => {
        if (!amount || amount === 0) return 0;
        const amountNum = parseFloat(amount) || 0;
        if (currency === 'TRY') return amountNum;
        const rate = rates[currency] || 1;  // rates kullan, exchangeRates değil
        return amountNum * rate;
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      currentMonthEnd.setHours(23, 59, 59, 999);

      let pendingPayments = 0;
      let overduePayments = 0;
      let totalRevenue = 0;
      let totalExpenses = 0;

      const activeRecurringPayments = (companyPayments || []).filter(p => p.is_active !== false);
      
      // Şirket ödemelerinden bu ay beklenen gelirleri hesapla
      activeRecurringPayments.forEach(payment => {
        if (!payment.next_payment_date) return;
        const nextPaymentDate = new Date(payment.next_payment_date);
        
        // Bu ayın ödemesi mi kontrol et
        if (nextPaymentDate >= currentMonthStart && nextPaymentDate <= currentMonthEnd) {
          const amount = parseFloat(payment.sale_price) || 0;
          const currency = payment.sale_currency || 'TRY';
          const amountInTRY = convertToTRY(amount, currency);
          totalRevenue += amountInTRY;
        }
      });
      
      // Kasa işlemlerinden bu ayın giderlerini hesapla (sadece expense tipi)
      (transactions || []).forEach(transaction => {
        if (!transaction.transaction_date) return;
        const transactionDate = new Date(transaction.transaction_date);
        
        // Bu ayın işlemi mi kontrol et
        if (transactionDate >= currentMonthStart && transactionDate <= currentMonthEnd) {
          const amount = parseFloat(transaction.amount) || 0;
          const currency = transaction.currency || 'TRY';
          const amountInTRY = convertToTRY(amount, currency);
          
          // Önce transaction_type'a bak, yoksa amount'un işaretine bak
          if (transaction.transaction_type === 'expense') {
            totalExpenses += Math.abs(amountInTRY);
          } else if (transaction.transaction_type !== 'income' && amount < 0) {
            // transaction_type yoksa ve amount negatifse
            totalExpenses += Math.abs(amountInTRY);
          }
        }
      });

      // Son aktiviteleri hazırla
      const activities = [];
      
      activeRecurringPayments.forEach(payment => {
        if (!payment.next_payment_date) return;
        
        const nextPaymentDate = new Date(payment.next_payment_date);
        nextPaymentDate.setHours(0, 0, 0, 0);
        
        const daysUntil = Math.ceil((nextPaymentDate - today) / (1000 * 60 * 60 * 24));
        
        if (nextPaymentDate < today) {
          overduePayments++;
          activities.push({
            type: 'overdue',
            company: payment.company_name || 'Bilinmeyen',
            package: payment.package_name || payment.domain_name || '-',
            amount: payment.sale_price || 0,
            currency: payment.sale_currency || 'TRY',
            date: payment.next_payment_date,
            daysOverdue: Math.abs(daysUntil)
          });
        }
        
        if (nextPaymentDate >= currentMonthStart && nextPaymentDate <= currentMonthEnd) {
          pendingPayments++;
          
          activities.push({
            type: 'pending',
            company: payment.company_name || 'Bilinmeyen',
            package: payment.package_name || payment.domain_name || '-',
            amount: payment.sale_price || 0,
            currency: payment.sale_currency || 'TRY',
            date: payment.next_payment_date,
            daysUntil
          });
        }
      });

      // Son 6 ayın verilerini hazırla - Kasa (account_transactions) tablosundan
      const last6Months = [];
      
      // Filter transactions by selected account if any
      const filteredTransactions = defaultAccountId
        ? (transactions || []).filter(t => t.account_id === defaultAccountId)
        : (transactions || []);
      
      // Show warning if no account selected
      setShowAccountWarning(!defaultAccountId);
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        
        let monthRevenue = 0;
        let monthExpenses = 0;
        
        // Kasa işlemlerinden gelir/gider hesapla (seçili hesap veya tüm hesaplar)
        filteredTransactions.forEach(transaction => {
          if (!transaction.transaction_date) return;
          const transactionDate = new Date(transaction.transaction_date);
          
          // İşlem tarihi bu ay içinde mi kontrol et
          if (transactionDate >= date && transactionDate <= monthEnd) {
            const amount = parseFloat(transaction.amount) || 0;
            const currency = transaction.currency || 'TRY';
            const amountInTRY = convertToTRY(amount, currency);
            
            // İşlem türüne göre gelir veya gider ekle
            // transaction_type: 'income' (gelir), 'expense' (gider)
            // Önce transaction_type'a bak, yoksa amount'un işaretine bak
            if (transaction.transaction_type === 'expense') {
              // Gider işlemi
              monthExpenses += Math.abs(amountInTRY);
            } else if (transaction.transaction_type === 'income') {
              // Gelir işlemi
              monthRevenue += Math.abs(amountInTRY);
            } else {
              // transaction_type yoksa, amount'un işaretine bak
              if (amount < 0) {
                monthExpenses += Math.abs(amountInTRY);
              } else {
                monthRevenue += amountInTRY;
              }
            }
          }
        });
        
        last6Months.push({
          month: date.toLocaleDateString('tr-TR', { month: 'short' }),
          revenue: Math.round(monthRevenue),
          expenses: Math.round(monthExpenses),
          profit: Math.round(monthRevenue - monthExpenses)
        });
      }
      setMonthlyData(last6Months);

      // Gelir türüne göre dağılım
      const typeDistribution = {};
      activeRecurringPayments.forEach(payment => {
        const type = payment.package_type || 'other';
        const amount = convertToTRY(payment.sale_price || 0, payment.sale_currency || 'TRY');
        typeDistribution[type] = (typeDistribution[type] || 0) + amount;
      });

      const typeColors = {
        domain: '#3B82F6',
        hosting: '#10B981',
        ssl: '#F59E0B',
        email: '#EF4444',
        reklam: '#8B5CF6',
        danismanlik: '#EC4899',
        other: '#6B7280'
      };

      const typeData = Object.entries(typeDistribution).map(([type, value]) => ({
        name: type === 'domain' ? 'Domain' : 
              type === 'hosting' ? 'Hosting' :
              type === 'ssl' ? 'SSL' :
              type === 'email' ? 'E-posta' :
              type === 'reklam' ? 'Reklam' :
              type === 'danismanlik' ? 'Danışmanlık' : 'Diğer',
        value: Math.round(value),
        color: typeColors[type] || '#6B7280'
      }));
      setRevenueByType(typeData);

      // Son aktiviteleri sırala ve limit
      setRecentActivities(activities.slice(0, 10));

      setAccounts(accounts || []);
      setCompanyPayments(companyPayments || []);

      setStats({
        totalDomains: domains.length,
        totalCustomers: customers.length,
        totalServers: servers.length,
        pendingPayments,
        overduePayments,
        totalRevenue,
        totalExpenses,
        netProfit: totalRevenue - totalExpenses
      });
      
    } catch (error) {
      console.error('Dashboard veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'TRY': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£', 'GOLD': 'Altın' };
    return symbols[currency] || currency;
  };

  // Animasyonlu StatCard bileşeni
  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend, trendValue }) => {
    const [animatedValue, setAnimatedValue] = useState(0);
    
    useEffect(() => {
      let targetValue;
      if (typeof value === 'number') {
        targetValue = value;
      } else if (typeof value === 'string') {
        // String değerini doğru parse et - ₺16.676 gibi değerler için
        // Önce ₺ işaretini kaldır, sonra nokta yerine virgül koy ve parse et
        const cleaned = value.replace(/[^\d.,-]/g, '');
        // Türkçe format için: nokta binlik ayracı, virgül ondalık ayracı
        // "16.676" -> 16676
        targetValue = parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
      } else {
        targetValue = 0;
      }
      
      const duration = 1000;
      const steps = 30;
      const stepValue = targetValue / steps;
      let currentStep = 0;
      
      const timer = setInterval(() => {
        currentStep++;
        setAnimatedValue(Math.min(stepValue * currentStep, targetValue));
        if (currentStep >= steps) clearInterval(timer);
      }, duration / steps);
      
      return () => clearInterval(timer);
    }, [value]);

    return (
      <div className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <div className={`absolute top-0 right-0 w-32 h-32 ${color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`}></div>
        <div className="relative">
          <div className="flex items-start justify-between">
            <div className={`p-3 rounded-xl ${color} bg-opacity-20`}>
              <Icon className={`h-6 w-6 ${color.replace('bg-', 'text-')}`} />
            </div>
            {trend && (
              <div className={`flex items-center text-sm font-medium ${
                trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {trend === 'up' ? <ArrowUpRight className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
              {typeof value === 'string' && value.includes('₺') ? (
                <>₺{animatedValue.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</>
              ) : (
                <>{animatedValue.toLocaleString('tr-TR')}</>
              )}
            </p>
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ₺{entry.value.toLocaleString('tr-TR')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-12 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Genel bakış ve istatistikler</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm text-gray-500 dark:text-gray-400">Son güncelleme</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button 
                onClick={fetchDashboardData}
                className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                <Activity className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        {/* Stats Grid - Includes Account Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
          <StatCard
            title="Toplam Domain"
            value={stats.totalDomains}
            icon={Globe}
            color="bg-blue-500"
            subtitle="Aktif domain"
            trend="up"
            trendValue="+12%"
          />
          <StatCard
            title="Toplam Müşteri"
            value={stats.totalCustomers}
            icon={Users}
            color="bg-green-500"
            subtitle="Kayıtlı müşteri"
            trend="up"
            trendValue="+8%"
          />
          <StatCard
            title="Toplam Sunucu"
            value={stats.totalServers}
            icon={Server}
            color="bg-purple-500"
            subtitle="Aktif sunucu"
          />
          <StatCard
            title="Net Kar"
            value={`₺${stats.netProfit.toLocaleString()}`}
            icon={TrendingUp}
            color="bg-emerald-500"
            subtitle="Bu ay"
            trend={stats.netProfit >= 0 ? 'up' : 'down'}
            trendValue={stats.netProfit >= 0 ? '+15%' : '-5%'}
          />
          
          {/* Account Cards - Integrated into the same grid */}
          {accounts.map(account => {
            const balance = parseFloat(account.balance || 0);
            const isPositive = balance >= 0;
            const currencySymbol = getCurrencySymbol(account.currency);
            
            return (
              <div
                key={account.id}
                className={`group relative overflow-hidden rounded-2xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  isPositive
                    ? 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800'
                    : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800'
                }`}
              >
                <div className={`absolute top-0 right-0 w-32 h-32 ${
                  isPositive ? 'bg-emerald-500' : 'bg-red-500'
                } opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`}></div>
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${
                      isPositive ? 'bg-emerald-500 bg-opacity-20' : 'bg-red-500 bg-opacity-20'
                    }`}>
                      <Wallet className={`h-6 w-6 ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} />
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      account.is_active === false
                        ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                      {account.is_active === false ? 'Pasif' : 'Aktif'}
                    </div>
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{account.name}</p>
                  <p className={`text-2xl font-bold ${
                    isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {currencySymbol} {balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{account.currency}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Revenue & Expenses Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
          <StatCard
            title="Bekleyen Ödemeler"
            value={stats.pendingPayments}
            icon={CreditCard}
            color="bg-yellow-500"
            subtitle="Bu ay"
          />
          <StatCard
            title="Gecikmiş Ödemeler"
            value={stats.overduePayments}
            icon={AlertTriangle}
            color="bg-red-500"
            subtitle="Vadesi geçen"
          />
          <StatCard
            title="Toplam Gelir"
            value={`₺${Math.round(stats.totalRevenue).toLocaleString('tr-TR')}`}
            icon={DollarSign}
            color="bg-green-500"
            subtitle="Bu ay beklenen"
          />
          <StatCard
            title="Toplam Gider"
            value={`₺${Math.round(stats.totalExpenses).toLocaleString('tr-TR')}`}
            icon={TrendingDown}
            color="bg-orange-500"
            subtitle="Bu ay beklenen"
          />
          <StatCard
            title="Kar"
            value={`₺${Math.round(Math.max(0, stats.netProfit)).toLocaleString('tr-TR')}`}
            icon={TrendingUp}
            color={stats.netProfit > 0 ? "bg-green-500" : "bg-gray-400"}
            subtitle={stats.netProfit > 0 ? "Bu ay kar" : "Kar yok"}
            trend={stats.netProfit > 0 ? "up" : undefined}
            trendValue={stats.netProfit > 0 ? "+Artı" : undefined}
          />
          <StatCard
            title="Zarar"
            value={`₺${Math.round(Math.abs(Math.min(0, stats.netProfit))).toLocaleString('tr-TR')}`}
            icon={AlertTriangle}
            color={stats.netProfit < 0 ? "bg-red-500" : "bg-gray-400"}
            subtitle={stats.netProfit < 0 ? "Bu ay zarar" : "Zarar yok"}
            trend={stats.netProfit < 0 ? "down" : undefined}
            trendValue={stats.netProfit < 0 ? "Risk" : undefined}
          />
        </div>

        {/* Account Selection Warning */}
        {showAccountWarning && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                  Dashboard hesap seçilmedi
                </h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-3">
                  "Aylık Gelir & Gider" grafiği için varsayılan bir hesap seçmediniz. Tüm hesapların işlemleri gösteriliyor.
                  Belirli bir hesabın işlemlerini görmek için Ayarlar sayfasından varsayılan hesap seçin.
                </p>
                <a
                  href="/settings"
                  className="inline-flex items-center space-x-2 text-sm font-medium text-yellow-900 dark:text-yellow-200 hover:text-yellow-700 dark:hover:text-yellow-100 transition-colors"
                >
                  <SettingsIcon className="h-4 w-4" />
                  <span>Ayarlar sayfasına git</span>
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Charts Row - 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Monthly Revenue Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Aylık Gelir & Gider</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Son 6 ay {showAccountWarning && <span className="text-yellow-600 dark:text-yellow-400">(Tüm hesaplar)</span>}
                </p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} />
                <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fillOpacity={1} fill="url(#colorRevenue)" name="Gelir" />
                <Area type="monotone" dataKey="expenses" stroke="#EF4444" fillOpacity={1} fill="url(#colorExpenses)" name="Gider" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue by Type */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gelir Dağılımı</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Hizmet türüne göre</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <PieChart className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPieChart>
                <Pie
                  data={revenueByType}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {revenueByType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `₺${value.toLocaleString('tr-TR')}`}
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>

          {/* Payment Status Distribution - New Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ödeme Durumu</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Dağılım</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <PieChart className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPieChart>
                <Pie
                  data={[
                    { name: 'Bekleyen', value: stats.pendingPayments, color: '#F59E0B' },
                    { name: 'Gecikmiş', value: stats.overduePayments, color: '#EF4444' },
                    { name: 'Tamamlandı', value: Math.max(0, companyPayments.length - stats.pendingPayments - stats.overduePayments), color: '#10B981' }
                  ].filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {[
                    { name: 'Bekleyen', value: stats.pendingPayments, color: '#F59E0B' },
                    { name: 'Gecikmiş', value: stats.overduePayments, color: '#EF4444' },
                    { name: 'Tamamlandı', value: Math.max(0, companyPayments.length - stats.pendingPayments - stats.overduePayments), color: '#10B981' }
                  ].filter(d => d.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value} adet`}
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row - 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Profit Trend Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kar Trendi</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Aylık net kar değişimi</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  ₺{stats.netProfit.toLocaleString('tr-TR')}
                </span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} />
              <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', r: 5 }}
                activeDot={{ r: 7 }}
                name="Net Kar"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

          {/* Revenue Comparison Chart - New */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gelir/Gider Karşılaştırması</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Son 6 ay</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <BarChart3 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} />
                <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="revenue" fill="#3B82F6" name="Gelir" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expenses" fill="#EF4444" name="Gider" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* KPI Cards Row - New */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Kar Marjı</p>
                <p className="text-3xl font-bold mt-1">
                  {stats.totalRevenue > 0 ? `%${((stats.netProfit / stats.totalRevenue) * 100).toFixed(1)}` : '%0'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Ortalama Gelir</p>
                <p className="text-3xl font-bold mt-1">
                  ₺{companyPayments.length > 0 ? (stats.totalRevenue / companyPayments.length).toFixed(0) : '0'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Aktif Oran</p>
                <p className="text-3xl font-bold mt-1">
                  %{companyPayments.length > 0 ? ((companyPayments.filter(p => p.is_active !== false).length) / companyPayments.length * 100).toFixed(0) : '0'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Activity className="h-6 w-6" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Toplam Paket</p>
                <p className="text-3xl font-bold mt-1">{companyPayments.length}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Package className="h-6 w-6" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Kar Durumu</p>
                <p className="text-3xl font-bold mt-1">
                  {stats.netProfit > 0 ? `₺${stats.netProfit.toLocaleString()}` : '₺0'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Zarar Durumu</p>
                <p className="text-3xl font-bold mt-1">
                  {stats.netProfit < 0 ? `₺${Math.abs(stats.netProfit).toLocaleString()}` : '₺0'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <AlertTriangle className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Son Aktiviteler</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Ödeme hatırlatmaları</p>
            </div>
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Activity className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="space-y-3">
            {recentActivities.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Yaklaşan aktivite yok</p>
              </div>
            ) : (
              recentActivities.map((activity, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
                    activity.type === 'overdue' 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'overdue' 
                        ? 'bg-red-500' 
                        : 'bg-yellow-500'
                    }`}>
                      {activity.type === 'overdue' ? (
                        <AlertTriangle className="h-4 w-4 text-white" />
                      ) : (
                        <Calendar className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{activity.company}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{activity.package}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {getCurrencySymbol(activity.currency)} {parseFloat(activity.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className={`text-sm ${
                      activity.type === 'overdue' 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {activity.type === 'overdue' 
                        ? `${activity.daysOverdue} gün gecikmiş` 
                        : `${activity.daysUntil} gün kaldı`
                      }
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
